// src/components/common/RequireAdmin.tsx
//
// Route guard for the admin dashboard. It:
//   1. Redirects to /sign-in when there is no token.
//   2. Hydrates the current user from /auth/me when a token exists.
//   3. Blocks non-admin users with an "access denied" screen.
import React from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { Loader2, ShieldAlert } from "lucide-react";
import { useAppSelector } from "../../app/hooks";
import { selectCurrentUser } from "../../app/features/authSlice";
import { useGetCurrentUserQuery } from "../../app/api/authApi";

const RequireAdmin: React.FC = () => {
  const location = useLocation();
  const token = useAppSelector((s: any) => s.auth.token as string | null);
  const user = useAppSelector(selectCurrentUser);

  // Fetch the user when a token exists but the store isn't hydrated yet.
  const { isLoading, isFetching, isError } = useGetCurrentUserQuery(undefined, {
    skip: !token,
  });

  if (!token) {
    return <Navigate to="/sign-in" replace state={{ from: location }} />;
  }

  if ((isLoading || isFetching) && !user) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3 text-slate-500">
        <Loader2 className="h-8 w-8 animate-spin" />
        <p className="text-sm font-medium">Verifying access…</p>
      </div>
    );
  }

  if (isError && !user) {
    return <Navigate to="/sign-in" replace state={{ from: location }} />;
  }

  if (user && user.role !== "admin") {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 px-4 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-red-50 text-red-500">
          <ShieldAlert className="h-7 w-7" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-slate-900">Access denied</h1>
          <p className="mt-1 max-w-sm text-sm text-slate-500">
            This area is restricted to administrators. If you believe this is a
            mistake, contact the platform owner.
          </p>
        </div>
        <a
          href="/"
          className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
        >
          Back to site
        </a>
      </div>
    );
  }

  // Authenticated admin (or token present and hydrating an admin already in store).
  return <Outlet />;
};

export default RequireAdmin;
